/**
 * Hersch Design - Scroll Animation System
 * Intersection Observer based animations with parallax support
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
    staggerDelay: 100,
    parallaxSpeed: 0.3
  };

  // Track initialized elements
  const initialized = new Set();

  /**
   * Initialize scroll animations
   */
  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('[data-animate]');

    if (!animatedElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initialized.has(entry.target)) {
          initialized.add(entry.target);
          animateElement(entry.target);
        }
      });
    }, {
      threshold: config.threshold,
      rootMargin: config.rootMargin
    });

    animatedElements.forEach(el => {
      // Set initial state
      el.classList.add('animate-hidden');
      observer.observe(el);
    });
  }

  /**
   * Animate a single element
   */
  function animateElement(element) {
    const animationType = element.dataset.animate || 'fade-up';
    const delay = parseInt(element.dataset.animateDelay) || 0;

    setTimeout(() => {
      element.classList.remove('animate-hidden');
      element.classList.add('animate-visible', `animate-${animationType}`);
    }, delay);
  }

  /**
   * Initialize staggered animations for child elements
   */
  function initStaggeredAnimations() {
    const staggerContainers = document.querySelectorAll('[data-stagger]');

    if (!staggerContainers.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initialized.has(entry.target)) {
          initialized.add(entry.target);
          animateStaggeredChildren(entry.target);
        }
      });
    }, {
      threshold: config.threshold,
      rootMargin: config.rootMargin
    });

    staggerContainers.forEach(container => {
      const children = container.querySelectorAll('[data-stagger-item]');
      children.forEach(child => {
        child.classList.add('animate-hidden');
      });
      observer.observe(container);
    });
  }

  /**
   * Animate children with staggered delay
   */
  function animateStaggeredChildren(container) {
    const children = container.querySelectorAll('[data-stagger-item]');
    const baseDelay = parseInt(container.dataset.staggerDelay) || config.staggerDelay;

    children.forEach((child, index) => {
      setTimeout(() => {
        child.classList.remove('animate-hidden');
        child.classList.add('animate-visible', 'animate-fade-up');
      }, index * baseDelay);
    });
  }

  /**
   * Initialize parallax effects
   */
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');

    if (!parallaxElements.length) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let ticking = false;

    function updateParallax() {
      parallaxElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Only animate when element is in view
        if (rect.bottom < 0 || rect.top > windowHeight) return;

        const speed = parseFloat(element.dataset.parallaxSpeed) || config.parallaxSpeed;
        const direction = element.dataset.parallaxDirection || 'up';

        // Calculate offset based on scroll position
        const scrollPercent = (windowHeight - rect.top) / (windowHeight + rect.height);
        const offset = (scrollPercent - 0.5) * 100 * speed;

        let transform;
        switch (direction) {
          case 'down':
            transform = `translateY(${-offset}px)`;
            break;
          case 'left':
            transform = `translateX(${offset}px)`;
            break;
          case 'right':
            transform = `translateX(${-offset}px)`;
            break;
          default: // up
            transform = `translateY(${offset}px)`;
        }

        element.style.transform = transform;
      });

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    updateParallax(); // Initial position
  }

  /**
   * Initialize counter animations
   */
  function initCounterAnimations() {
    const counters = document.querySelectorAll('[data-counter]');

    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initialized.has(entry.target)) {
          initialized.add(entry.target);
          animateCounter(entry.target);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(counter => observer.observe(counter));
  }

  /**
   * Animate a counter element
   */
  function animateCounter(element) {
    const target = parseInt(element.dataset.counter);
    const duration = parseInt(element.dataset.counterDuration) || 2000;
    const start = 0;
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * easeProgress);

      element.textContent = current.toLocaleString();

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  /**
   * Initialize reveal animations for text
   */
  function initTextReveal() {
    const textElements = document.querySelectorAll('[data-text-reveal]');

    if (!textElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initialized.has(entry.target)) {
          initialized.add(entry.target);
          entry.target.classList.add('text-reveal-active');
        }
      });
    }, { threshold: 0.2 });

    textElements.forEach(el => observer.observe(el));
  }

  /**
   * Initialize all animations
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  }

  function initAll() {
    initScrollAnimations();
    initStaggeredAnimations();
    initParallax();
    initCounterAnimations();
    initTextReveal();
  }

  // Start
  init();

  // Re-initialize on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function() {
    initialized.clear();
    initAll();
  });

})();
